import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Download,
  HelpCircle,
  Users,
  Lock,
  Eye,
  Trash2,
  ExternalLink,
  Copy,
  Check,
  QrCode
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Layout } from "../components/Layout";
import { uploadCensus, getConsulta, getCensusStats, deleteCensus, downloadCensusTemplate, getCensusUploadStatus, clearCensusUploadStatus } from "../lib/api";
import { toast } from "sonner";
import { VotingQRCode } from "../components/VotingQRCode";

export default function CensusPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadErrors, setUploadErrors] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [consulta, setConsulta] = useState(null);
  const [censusStats, setCensusStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCensusLink, setCopiedCensusLink] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const pollingRef = useRef(null);

  const canEdit = consulta?.status === "draft";
  const censusCheckUrl = `${window.location.origin}/census-check/${id}`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [consultaRes, statsRes] = await Promise.all([
          getConsulta(id),
          getCensusStats(id).catch(() => ({ data: { total: 0, registered: 0, voted: 0, participation: 0 } }))
        ]);
        setConsulta(consultaRes.data);
        setCensusStats(statsRes.data);
      } catch (error) {
        toast.error(t('errors.loadingData'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (!canEdit) return;
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (selectedFile) => {
    if (!canEdit) return;
    
    const extension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
    
    if (extension !== '.xlsx') {
      toast.error(t('census.onlyXlsx'));
      return;
    }
    setFile(selectedFile);
    setResult(null);
    setUploadErrors(null);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = () => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const res = await getCensusUploadStatus(id);
        const data = res.data;
        setUploadProgress(data);

        if (data.status === "completed") {
          stopPolling();
          setResult(data.result);
          setUploading(false);
          setUploadProgress(null);
          const statsRes = await getCensusStats(id);
          setCensusStats(statsRes.data);
          toast.success(t('census.loadedSuccess'));
          await clearCensusUploadStatus(id);
        } else if (data.status === "error") {
          stopPolling();
          setUploading(false);
          setUploadProgress(null);
          if (data.errors && data.errors.length > 0) {
            setUploadErrors({ code: "census_errors", message: data.error_message, errors: data.errors });
            toast.error(t('census.errorsFound', { count: data.errors.length }));
          } else {
            toast.error(data.error_message || t('census.loadError'));
          }
          await clearCensusUploadStatus(id);
        }
      } catch {
        // Polling error - continue polling
      }
    }, 2000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, []);

  const handleUpload = async () => {
    if (!file || !canEdit) return;

    setUploading(true);
    setUploadErrors(null);
    setResult(null);
    setUploadProgress({ status: "processing", total: 0, processed: 0 });
    try {
      const response = await uploadCensus(id, file);
      if (response.data?.status === "processing") {
        setUploadProgress({ status: "processing", total: response.data.total, processed: 0 });
        startPolling();
      } else {
        setResult(response.data);
        setUploading(false);
        const statsRes = await getCensusStats(id);
        setCensusStats(statsRes.data);
        toast.success(t('census.loadedSuccess'));
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'census_errors') {
        setUploadErrors(detail);
        toast.error(t('census.errorsFound', { count: detail.errors?.length || 0 }));
      } else {
        toast.error(typeof detail === 'string' ? detail : t('census.loadError'));
      }
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await downloadCensusTemplate(id);
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disposition = response.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename=(.+)/);
      a.download = match ? match[1] : "plantilla_cens.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t('census.templateError'));
    }
  };

  const handleDeleteCensus = async () => {
    if (!window.confirm(t('censusPage.confirmDeleteCensus'))) return;
    try {
      await deleteCensus(id);
      toast.success(t('census.deletedSuccess'));
      const statsRes = await getCensusStats(id).catch(() => ({ data: { total: 0, registered: 0, voted: 0, participation: 0 } }));
      setCensusStats(statsRes.data);
      setFile(null);
      setResult(null);
      setUploadErrors(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('census.deleteError'));
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: t('status.draft'),
      pending_publication: t('status.pendingPublication'),
      pending_closure: t('status.pendingClosure'),
      published: t('status.published'),
      closed: t('status.closed'),
      pending_blockchain: t('status.pendingBlockchain'),
      expired: t('status.expired'),
      scrutinized: t('status.scrutinized')
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Layout>
        <div className="content-container max-w-3xl flex items-center justify-center py-20">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="content-container max-w-3xl" data-testid="census-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            onClick={() => navigate(`/consultes/${id}`)}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('censusPage.backToConsulta')}
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold text-slate-900">
                {canEdit ? t('censusPage.manageCensus') : t('censusPage.viewCensus')}
              </h1>
              {!canEdit && (
                <span className="inline-flex items-center gap-1 px-2 py-1 mt-2 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                  <Eye className="h-3 w-3" />
                  {t('censusPage.readOnly')}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {file && !result && canEdit && (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleUpload}
                  disabled={uploading}
                  data-testid="upload-btn"
                >
                  {uploading ? (
                    <>
                      <div className="spinner mr-2" />
                      {t('censusPage.uploading')}
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      {t('censusPage.uploadCensus')}
                    </>
                  )}
                </Button>
              )}
              {censusStats?.total > 0 && (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => navigate(`/consultes/${id}`)}
                  data-testid="continue-to-consulta-btn"
                >
                  {t('censusPage.continueToConsulta')}
                </Button>
              )}
            </div>
          </div>

          {/* Upload Progress Bar */}
          {uploading && uploadProgress && uploadProgress.total > 0 && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="upload-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-700">
                  {t('censusPage.processingCensus', { processed: uploadProgress.processed || 0, total: uploadProgress.total })}
                </span>
                <span className="text-sm font-medium text-blue-700">
                  {uploadProgress.total > 0 ? Math.round(((uploadProgress.processed || 0) / uploadProgress.total) * 100) : 0}%
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress.total > 0 ? ((uploadProgress.processed || 0) / uploadProgress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          <p className="text-slate-500 mt-1">
            {canEdit 
              ? t('censusPage.uploadDescription')
              : t('censusPage.consultaInState', { title: consulta?.title, status: getStatusLabel(consulta?.status) })
            }
          </p>
        </motion.div>

        {/* Read-only warning */}
        {!canEdit && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6"
          >
            <div className="flex items-center gap-3">
              <Lock className="h-5 w-5 text-amber-600" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  {t('censusPage.censusLocked')}
                </p>
                <p className="text-xs text-amber-700">
                  {t('censusPage.censusLockedDesc')}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Census Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
        >
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{censusStats?.total || 0}</p>
                <p className="text-sm text-slate-500">{t('results.totalCensus')}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{censusStats?.voted || 0}</p>
                <p className="text-sm text-slate-500">{t('results.haveVoted')}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {censusStats?.participation?.toFixed(1) || 0}%
                </p>
                <p className="text-sm text-slate-500">{t('results.participation')}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Census Check Link - show when census has data */}
        {censusStats?.total > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.11 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
          >
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 mb-1">
                  {t('censusPage.censusCheckLink')}
                </p>
                <p className="text-xs text-blue-700 mb-3">
                  {t('censusPage.censusCheckLinkDesc')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex gap-2">
                      <div className="flex-1 p-2 bg-white rounded-lg border border-blue-200 font-mono text-xs truncate">
                        {censusCheckUrl}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-blue-200 text-blue-700 hover:bg-blue-100"
                        data-testid="copy-census-check-url-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(censusCheckUrl);
                          setCopiedCensusLink(true);
                          setTimeout(() => setCopiedCensusLink(false), 2000);
                          toast.success(t('common.linkCopied'));
                        }}
                      >
                        {copiedCensusLink ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      </Button>
                      <a href={censusCheckUrl} target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm" className="border-blue-200 text-blue-700 hover:bg-blue-100">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </a>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <VotingQRCode url={censusCheckUrl} title={t('censusCheck.title')} size={100} />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Delete Census Button - only show if can edit and has census */}
        {canEdit && censusStats?.total > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <div>
                  <p className="text-sm font-medium text-red-900">
                    {t('censusPage.currentCensusRecords', { count: censusStats.total })}
                  </p>
                  <p className="text-xs text-red-700">
                    {t('censusPage.canDeleteAndReupload')}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteCensus}
                className="border-red-300 text-red-700 hover:bg-red-100"
                data-testid="delete-census-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('censusPage.deleteCensus')}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Instructions - only show if can edit and no census loaded yet */}
        {canEdit && (!censusStats || censusStats.total === 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6"
          >
            <div className="flex items-start gap-3">
              <HelpCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900 mb-2">{t('censusPage.fileFormat')}</h3>
                <p className="text-sm text-blue-700 mb-3" dangerouslySetInnerHTML={{ __html: t('censusPage.acceptedColumns') }} />
                <ul className="text-sm text-blue-700 space-y-1 mb-4">
                  <li dangerouslySetInnerHTML={{ __html: t('censusPage.colDni') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('censusPage.colBirthDate') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('censusPage.colSex') }} />
                </ul>
                <p className="text-xs text-blue-600 mb-4">
                  {t('censusPage.dateFormats')}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadTemplate}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="download-template-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('censusPage.downloadTemplate')}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Upload Area - only show if can edit */}
        {canEdit && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-slate-200 rounded-lg p-8"
          >
            <div
              className={`
                border-2 border-dashed rounded-lg p-12 text-center transition-colors
                ${dragActive ? "border-blue-500 bg-blue-50" : "border-slate-200"}
                ${file ? "bg-slate-50" : ""}
              `}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="drop-zone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                className="hidden"
                data-testid="file-input"
              />

              {file ? (
                <div>
                  <FileSpreadsheet className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
                  <p className="text-lg font-medium text-slate-900 mb-1">{file.name}</p>
                  <p className="text-sm text-slate-500 mb-4">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFile(null);
                      setResult(null);
                    }}
                    data-testid="remove-file-btn"
                  >
                    {t('censusPage.chooseAnotherFile')}
                  </Button>
                </div>
              ) : (
                <div>
                  <Upload className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-lg font-medium text-slate-700 mb-2">
                    {t('censusPage.dragFileHere')}
                  </p>
                  <p className="text-sm text-slate-500 mb-4">
                    {t('censusPage.acceptedFormat')}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="select-file-btn"
                  >
                    {t('censusPage.selectFile')}
                  </Button>
                </div>
              )}
            </div>

            {/* Upload Button removed - now in header */}

            {/* Result */}
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-6 bg-emerald-50 border border-emerald-200 rounded-lg"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-emerald-900 mb-2">
                      {t('censusPage.censusLoaded')}
                    </h3>
                    <div className="text-sm text-emerald-700 space-y-1">
                      <p>{t('censusPage.recordsAdded')}: <strong>{result.records_added}</strong></p>
                      <p>{t('censusPage.recordsUpdated')}: <strong>{result.records_updated}</strong></p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Upload Errors */}
            {uploadErrors && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-6 bg-red-50 border border-red-200 rounded-lg"
                data-testid="census-upload-errors"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-6 w-6 text-red-600 mt-0.5 shrink-0" />
                  <div className="w-full">
                    <h3 className="font-medium text-red-900 mb-2">
                      {uploadErrors.message}
                    </h3>
                    <div className="max-h-60 overflow-y-auto bg-white border border-red-100 rounded-lg">
                      {uploadErrors.errors?.map((err, i) => (
                        <div key={i} className={`px-4 py-2 text-sm text-red-800 ${i > 0 ? "border-t border-red-50" : ""}`}>
                          {err}
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-red-600 mt-3">
                      {t('censusPage.fixErrorsAndReupload')}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Security Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-6 flex items-start gap-3 text-sm text-slate-500"
        >
          <AlertCircle className="h-5 w-5 text-slate-400 mt-0.5" />
          <p>
            {t('censusPage.securityNote')}
          </p>
        </motion.div>
      </div>
    </Layout>
  );
}
